import Route from '@ember/routing/route';
import type Transition from '@ember/routing/transition';
import { type Registry as Services, service } from '@ember/service';
import type HotelBenchmarkInsightTermsAndConditionsController from 'lumen/controllers/hotel/benchmark-insight/terms-and-conditions';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';

export default class HotelBenchmarkInsightTermsAndConditionsRoute extends Route {
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare router: Services['router'];

  declare controller: HotelBenchmarkInsightTermsAndConditionsController;

  transitionHandler: ((transition: Transition) => void) | null = null;

  beforeModel(): void {
    const { benchmarkInsightTermsAndConditionsAccepted } =
      this.hotelAndMarketService.hotel;

    if (benchmarkInsightTermsAndConditionsAccepted) {
      this.router.transitionTo('hotel.benchmark-insight.index');
    }
  }

  preventTransitionToBenchmarkInsight(
    transition: Transition,
    controller: HotelBenchmarkInsightTermsAndConditionsController,
  ): void {
    // There is a bug where users can circumvent the terms and conditions overlay by clicking on the Benchmark Insight main navigation tab while
    // on the terms and conditions route. This bug occurs because we then navigate to the `hotel.benchmark-insight.index` route,
    // without triggering the `beforeModel` hook (which checks if we have accepted the terms and conditions).
    // To prevent this bug, we add this `routeWillChange` listener to abort any transition to `hotel.benchmark-insight.index`,
    // unless the user has accepted the terms and conditions.
    if (transition.to?.name === 'hotel.benchmark-insight.index') {
      // Staff users are not allowed to accept terms and conditions for a hotel, which is why we implemented the `viewAsStaffUser` query param.
      // So we need to check on that as well, so that the behaviour is the same for staff users and regular users.
      const hasAcceptedTermsAndConditions =
        controller.hasAcceptedTermsAndConditions ||
        transition.to?.queryParams['viewAsStaffUser'];

      if (!hasAcceptedTermsAndConditions) {
        transition.abort();
      }
    }
  }

  resetController(): void {
    if (this.transitionHandler) {
      this.router.off('routeWillChange', this.transitionHandler);
      this.transitionHandler = null;
    }
  }

  setupController(
    controller: HotelBenchmarkInsightTermsAndConditionsController,
    model: unknown,
    transition: Transition,
  ): void {
    super.setupController(controller, model, transition);

    if (!this.transitionHandler) {
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      this.transitionHandler = (transition: Transition) =>
        this.preventTransitionToBenchmarkInsight(transition, controller);

      this.router.on('routeWillChange', this.transitionHandler);
    }
  }
}
